import { Component, OnInit, ViewChild } from "@angular/core";
import { LanguageUtil } from "@app/shared/utils/language.util";
import { FilteredResult, FilterParam, HomeService } from "./home.service";
import { iif, of, switchMap, tap } from "rxjs";
import { KeywordAndDefinition } from "@app/models/keywordAndDefinition";
import { DrawerComponent } from "@app/shared/components/drawer/drawer.component";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { LoadingDialogService } from "@app/shared/components/loading/loading-dialog.service";
import { PageEvent } from "@angular/material/paginator";
import { every } from "lodash";
import { MatDialog } from "@angular/material/dialog";
import { CreateOrEditKeywordDialogComponent, CreateOrEditKeywordDialogComponentData } from "./dialogs/create-edit-keyword-dialog/create-edit-keyword-dialog.component";
import { DeleteKeywordConfirmationDialogComponent } from "./dialogs/delete-keyword-confirmation-dialog/delete-keyword-confirmation-dialog.component";

interface FilterDrawerState {
  selectedTags: string[]
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit {

  @ViewChild('filterDrawer') filterDrawer!: DrawerComponent;

  /************************************************************************************************************
   * @Localized variables
   ***********************************************************************************************************/
  SEARCH_PLACEHOLDER_LOCALIZED: string = $localize`:Search Placeholder|Search Placeholder@@SeachPlaceholderKeyword:Search for keywords`
  LANGUAGES_MAP_LOCALIZED = LanguageUtil.LANGUAGES_NAME_MAP_LOCALIZED

  /************************************************************************************************************
   * @Undefined variables
   ***********************************************************************************************************/
  selectedLanguage!: string;
  filteredKeywordsAndDefinitions!: FilteredResult<KeywordAndDefinition>
  tags!: string[]
  filterDrawerState: FilterDrawerState = {
    selectedTags: []
  }

  /************************************************************************************************************
   * @Predefined variables
   ***********************************************************************************************************/
  /** */
  languageCodes: string[] = Array.from(this.LANGUAGES_MAP_LOCALIZED.keys())
  /** */
  filter: FilterParam = {
    tags: [],
    search: "",
    pageIndex: 0,
    pageSize: 10
  }
  /** */
  isLoading: boolean = true
  /** */
  filterDrawerOpened: boolean = false

  /** */
  constructor(
    private _homeService: HomeService,
    private _loadingService: LoadingDialogService,
    private _dialog: MatDialog
  ) {
    this.selectedLanguage = this.languageCodes[0]
  }

  /** */
  ngOnInit(): void {
    
    // all keywords subscription
    this._homeService.keywordsAndDefinitionsByLanguage$.pipe(
      tap(() => this._loadingService.openDialog()),
      switchMap(() => this._homeService.tags$),
      tap(tags => this.tags = this.filter.tags = this.filterDrawerState.selectedTags = tags),
      tap(() => this._homeService.filter = this.filter),
      tap(() => this._loadingService.hideDialog())
    ).subscribe();

    // filtered subscription
    this._homeService.filteredKeywordsAndDefinitions$.subscribe(
      (filteredKeywordsAndDefinitions: FilteredResult<KeywordAndDefinition>) => this.filteredKeywordsAndDefinitions = filteredKeywordsAndDefinitions)
    
    // trigger
    this._homeService.languageCode = this.selectedLanguage;
  }

  /** */
  search(search: string) {
    this.filter.search = search
    this._homeService.filter = this.filter
  }

  /** */
  openFilterDrawer(opened: boolean) {
    this.filterDrawerOpened = opened;
  }

  /** */
  filterTagsHandle(tag: string, matCheckboxChange: MatCheckboxChange) {
    
    if(!matCheckboxChange.checked) {
      this.filterDrawerState.selectedTags = this.filterDrawerState.selectedTags.filter(selectedTag => selectedTag !== tag)
      return
    }

    if(this.filterDrawerState.selectedTags.includes(tag)) { return }

    this.filterDrawerState.selectedTags.push(tag)

  }

  /** */
  applyFilters() {
    this.filter.tags = this.filterDrawerState.selectedTags
    this._homeService.filter = this.filter
    this.openFilterDrawer(false)
  }

  /** */
  resetFilters() {
    this.filterDrawerState.selectedTags = this.tags
  }

  /** */
  openCreateDialog() {
    const dialogRef = this._dialog.open<CreateOrEditKeywordDialogComponent, CreateOrEditKeywordDialogComponentData>(
      CreateOrEditKeywordDialogComponent, { 
        data: { 
          mode: 'create',
          languageCode: this.selectedLanguage,
          allTags: this.tags
        },
        minWidth: 800,
        panelClass: 'custom-mat-dialog-container'
      }
    )

    dialogRef.afterClosed().subscribe(() => {
      this._homeService.languageCode = this.selectedLanguage
    })
  }

  /** */
  editKeyword(keywordAndDefinition: KeywordAndDefinition) {
    
    const dialogRef = this._dialog.open<CreateOrEditKeywordDialogComponent, CreateOrEditKeywordDialogComponentData>(
      CreateOrEditKeywordDialogComponent, { 
        data: { 
          mode: 'edit',
          languageCode: this.selectedLanguage,
          keywordAndDefinition: keywordAndDefinition,
          allTags: this.tags
        },
        minWidth: 800,
        panelClass: 'custom-mat-dialog-container'
      }
    )

    dialogRef.afterClosed().subscribe(() => {
      this._homeService.languageCode = this.selectedLanguage
    })

  }

  /** */
  deleteKeyword(keywordAndDefinition: KeywordAndDefinition) {
    const dialogRef = this._dialog.open<DeleteKeywordConfirmationDialogComponent>(
      DeleteKeywordConfirmationDialogComponent, {
        maxWidth: 800,
        panelClass: 'custom-mat-dialog-container'
      }
    )

    dialogRef.afterClosed().pipe(
      switchMap(response => iif(
          () => response,
          this._homeService.deleteKeyword(keywordAndDefinition.keywordId),
          of(undefined)
      )),
    ).subscribe(response => {
      if(response === undefined) { return }
      this._homeService.languageCode = this.selectedLanguage
    })
  }

  /** */
  changeLanguage(language: string) {
    this.selectedLanguage = language
    this._homeService.languageCode = this.selectedLanguage;
  }

  /** */
  changePage(pageEvent: PageEvent) {
    this.filter.pageIndex = pageEvent.pageIndex
    this.filter.pageSize = pageEvent.pageSize

    this._homeService.filter = this.filter
  }
}